import React, { useState, useEffect, useCallback } from 'react';
import {
  projectsAllApi,
  projectsPlanningApi,
  type ProjectDto,
  type PlanningItemDto,
} from '../../services/api';
import CreatePlanningItemModal from './CreatePlanningItemModal';

export type { PlanningItemDto };

function formatPeso(amount: number): string {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function getItemLevel(item: PlanningItemDto, items: PlanningItemDto[]): number {
  if (!item.parent_id) return 1;
  const parent = items.find((p) => p.id === item.parent_id);
  if (!parent) return 2;
  return parent.parent_id ? 3 : 2;
}

function buildHierarchy(items: PlanningItemDto[], projectId: string): PlanningItemDto[] {
  const filtered = items.filter((i) => i.project_id === projectId);
  const byParent = new Map<string | null, PlanningItemDto[]>();
  for (const item of filtered) {
    const pid = item.parent_id;
    if (!byParent.has(pid)) byParent.set(pid, []);
    byParent.get(pid)!.push(item);
  }
  Array.from(byParent.values()).forEach((arr) => arr.sort((a, b) => a.sort_order - b.sort_order));
  const result: PlanningItemDto[] = [];
  const add = (parentId: string | null) => {
    const children = byParent.get(parentId) || [];
    for (const c of children) {
      result.push(c);
      add(c.id);
    }
  };
  add(null);
  return result;
}

function totalDuration(items: PlanningItemDto[], projectId: string): number {
  return items
    .filter((i) => i.project_id === projectId)
    .reduce((sum, i) => sum + (i.duration_days || 0), 0);
}

function totalBudget(items: PlanningItemDto[], projectId: string): number {
  return items
    .filter((i) => i.project_id === projectId)
    .reduce((sum, i) => sum + (i.budget_amount || 0), 0);
}

export default function ProjectPlanningPage() {
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [planningItems, setPlanningItems] = useState<PlanningItemDto[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<PlanningItemDto | null>(null);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingPlanning, setLoadingPlanning] = useState(false);

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      const res = await projectsAllApi.list();
      if (res.data) setProjects(res.data);
      else setProjects([]);
    } catch {
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const loadPlanningItems = useCallback(async (projectId: string) => {
    if (!projectId) return;
    setLoadingPlanning(true);
    try {
      const res = await projectsPlanningApi.list(projectId);
      if (res.data) setPlanningItems(res.data);
      else setPlanningItems([]);
    } catch {
      setPlanningItems([]);
    } finally {
      setLoadingPlanning(false);
    }
  }, []);

  useEffect(() => {
    if (selectedProjectId) {
      loadPlanningItems(selectedProjectId);
    } else {
      setPlanningItems([]);
    }
  }, [selectedProjectId, loadPlanningItems]);

  const handleItemCreated = async (item: PlanningItemDto) => {
    if (!selectedProjectId) return;
    try {
      const res = await projectsPlanningApi.create(selectedProjectId, {
        name: item.name,
        duration_days: item.duration_days,
        budget_amount: item.budget_amount,
        parent_id: item.parent_id,
        notes: item.notes,
        sort_order: item.sort_order,
      });
      if (res.data) {
        setPlanningItems((prev) => [...prev, res.data!]);
      }
    } catch {
      /* ignore */
    }
    setEditingItem(null);
  };

  const handleItemUpdated = async (item: PlanningItemDto) => {
    if (!selectedProjectId) return;
    try {
      const res = await projectsPlanningApi.update(selectedProjectId, item.id, {
        name: item.name,
        duration_days: item.duration_days,
        budget_amount: item.budget_amount,
        parent_id: item.parent_id,
        notes: item.notes,
        sort_order: item.sort_order,
      });
      if (res.data) {
        setPlanningItems((prev) => prev.map((i) => (i.id === item.id ? res.data! : i)));
      }
    } catch {
      /* ignore */
    }
    setEditingItem(null);
  };

  const openEditModal = (item: PlanningItemDto) => {
    setEditingItem(item);
    setModalOpen(true);
  };

  const openAddModal = () => {
    setEditingItem(null);
    setModalOpen(true);
  };

  const handleItemDeleted = async (id: string) => {
    if (!selectedProjectId) return;
    const collectDescendantIds = (parentId: string, items: PlanningItemDto[]): string[] => {
      const ids: string[] = [];
      for (const i of items) {
        if (i.parent_id === parentId) {
          ids.push(i.id, ...collectDescendantIds(i.id, items));
        }
      }
      return ids;
    };
    const toDelete = [id, ...collectDescendantIds(id, planningItems)];
    try {
      for (const itemId of toDelete.reverse()) {
        await projectsPlanningApi.delete(selectedProjectId, itemId);
      }
      setPlanningItems((prev) => prev.filter((i) => !toDelete.includes(i.id)));
    } catch {
      /* ignore */
    }
  };

  const selectedProject = projects.find((p) => p.id === selectedProjectId);
  const hierarchy = selectedProjectId ? buildHierarchy(planningItems, selectedProjectId) : [];
  const consumedBudget = selectedProjectId ? totalBudget(planningItems, selectedProjectId) : 0;
  const totalDays = selectedProjectId ? totalDuration(planningItems, selectedProjectId) : 0;
  const projectBudget = selectedProject?.budget || 0;
  const budgetRemaining = Math.max(0, projectBudget - consumedBudget);
  const budgetUsagePercent = projectBudget > 0 ? Math.min(100, (consumedBudget / projectBudget) * 100) : 0;

  return (
    <div className="max-w-7xl mx-auto w-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Project Planning</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Create hierarchical planning items with duration and budget consumption
          </p>
        </div>
      </div>

      {/* Project selector */}
      <div className="mb-4 shrink-0">
        <label className="block text-sm font-medium text-gray-700 mb-1">Select Project</label>
        <select
          value={selectedProjectId}
          onChange={(e) => setSelectedProjectId(e.target.value)}
          className="w-full max-w-md rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke%3D%22%236b7280%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%222%22%20d%3D%22M19%209l-7%207-7-7%22%20%2F%3E%3C%2Fsvg%3E')] bg-[length:1.25rem] bg-[right_0.5rem_center] bg-no-repeat pr-10"
        >
          <option value="">— Choose a project —</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.project_name}
              {p.client_name ? ` (${p.client_name})` : ''}
            </option>
          ))}
        </select>
        {loadingProjects && (
          <p className="text-sm text-gray-500 mt-2">Loading projects...</p>
        )}
        {!loadingProjects && projects.length === 0 && (
          <p className="text-sm text-amber-600 mt-2">No projects yet. Create a project first in All Projects.</p>
        )}
      </div>

      {selectedProjectId && (
        <>
          {/* Budget summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 shrink-0">
            <div className="bg-white rounded-xl p-4 shadow-card border border-gray-100">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Project Budget</p>
              <p className="text-xl font-bold text-gray-900 mt-1">{formatPeso(projectBudget)}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-card border border-gray-100">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Budget Consumed</p>
              <p className="text-xl font-bold text-brand-600 mt-1">{formatPeso(consumedBudget)}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-card border border-gray-100">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Budget Remaining</p>
              <p className="text-xl font-bold text-emerald-600 mt-1">{formatPeso(budgetRemaining)}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-card border border-gray-100">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Duration</p>
              <p className="text-xl font-bold text-gray-900 mt-1">{totalDays} days</p>
            </div>
          </div>

          {/* Budget usage bar */}
          <div className="mb-4 shrink-0">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-gray-600">Budget usage</span>
              <span className="font-medium text-gray-900">{budgetUsagePercent.toFixed(0)}%</span>
            </div>
            <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  budgetUsagePercent > 100 ? 'bg-red-500' : budgetUsagePercent > 80 ? 'bg-amber-500' : 'bg-brand-500'
                }`}
                style={{ width: `${Math.min(100, budgetUsagePercent)}%` }}
              />
            </div>
          </div>

          {/* Add item button */}
          <div className="flex justify-between items-center mb-3 shrink-0">
            <h2 className="font-semibold text-gray-900">Planning Items (Parent → Child → Mid-level)</h2>
            <button
              type="button"
              onClick={openAddModal}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 transition-colors shrink-0"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Item
            </button>
          </div>

          {/* Planning items list */}
          <div className="flex-1 min-h-0 overflow-auto bg-white rounded-xl shadow-card border border-gray-100">
            {loadingPlanning ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mb-3" />
                <p className="text-gray-500 text-sm">Loading planning items...</p>
              </div>
            ) : hierarchy.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                  <svg
                    className="w-8 h-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                    />
                  </svg>
                </div>
                <p className="font-medium text-gray-700">No planning items yet</p>
                <p className="text-sm text-gray-500 mt-1">Add items to define phases, tasks, and budget allocation</p>
                <button
                  type="button"
                  onClick={openAddModal}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add First Item
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px]">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50/80">
                      <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                        Item
                      </th>
                      <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                        Level
                      </th>
                      <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                        Duration (Days)
                      </th>
                      <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                        Budget (₱)
                      </th>
                      <th className="w-24 py-3 px-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider" scope="col">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {hierarchy.map((item) => {
                      const level = getItemLevel(item, planningItems);
                      const levelLabel = level === 1 ? 'Parent' : level === 2 ? 'Child' : 'Mid-level';
                      const indent = level > 1 ? (level - 1) * 20 : 0;
                      return (
                        <tr key={item.id} className="hover:bg-gray-50/50 transition-colors">
                          <td className="py-3 px-4">
                            <div style={{ paddingLeft: indent }} className="flex items-center gap-2">
                              {level > 1 && (
                                <span className="text-gray-400">
                                  {level === 2 ? '↳' : '↳↳'}
                                </span>
                              )}
                              <span className="font-medium text-gray-900">{item.name}</span>
                              {item.notes && (
                                <span className="text-xs text-gray-500 truncate max-w-[200px]" title={item.notes}>
                                  — {item.notes}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span
                              className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                level === 1 ? 'bg-blue-100 text-blue-700' : level === 2 ? 'bg-sky-100 text-sky-700' : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {levelLabel}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right font-medium text-gray-900">
                            {item.duration_days}
                          </td>
                          <td className="py-3 px-4 text-right font-medium text-gray-900">
                            {formatPeso(item.budget_amount)}
                          </td>
                          <td className="py-3 px-2">
                            <div className="flex items-center justify-end gap-0.5">
                              <button
                                type="button"
                                onClick={() => openEditModal(item)}
                                className="p-2 rounded-lg text-gray-400 hover:bg-brand-50 hover:text-brand-600 transition-colors"
                                aria-label="Edit"
                                title="Edit"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (window.confirm('Delete this item and its children? This cannot be undone.')) {
                                    handleItemDeleted(item.id);
                                  }
                                }}
                                className="p-2 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                                aria-label="Delete"
                                title="Delete"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <CreatePlanningItemModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingItem(null);
        }}
        onSuccess={editingItem ? handleItemUpdated : handleItemCreated}
        projectId={selectedProjectId}
        existingItems={planningItems.filter((i) => i.project_id === selectedProjectId)}
        maxLevel={3}
        editingItem={editingItem}
      />
    </div>
  );
}
